<template>
  <div>
    <v-dialog
      :attach="attach"
      :value="value"
      :fullscreen="isMobile"
      :max-width="isMobile ? undefined : width"
      :transition="isMobile ? undefined : 'slide-x-reverse-transition'"
      no-click-animation
      content-class="core-drawer"
      @keydown.esc="emitClose"
      @input="emitClose"
    >
      <v-card
        data-app
        class="core-drawer__body"
        :disabled="!value"
        :data-qa="dataQa"
      >
        <div class="core-drawer__header">
          <div class="d-flex align-center">
            <v-btn
              icon
              text
              color="icon-secondary"
              :data-qa="`${dataQa}-close`"
              @click="isBack ? emitBack() : emitClose()"
            >
              <v-icon v-if="isBack">
                ui-icon-arrow-back
              </v-icon>
              <v-icon v-else>
                {{ iconClose }}
              </v-icon>
            </v-btn>
            <div
              class="d-flex align-center text-h6 mx-4"
              :class="{ 'text-truncate': titleTruncate }"
              data-qa="core-drawer-title"
            >
              {{ title }}

              <slot name="title-append" />
            </div>
            <slot name="append-header" />
          </div>
          <slot name="header" />
        </div>
        <div class="core-drawer__main">
          <slot />
        </div>
        <div
          v-if="slots.footer"
          class="core-drawer__footer"
        >
          <slot name="footer" />
        </div>
        <slot name="overlay" />
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
/**
 * CoreDrawer — боковая панель справа (desktop) / fullscreen (mobile).
 *
 * @example
 *   <CoreDrawer v-model="isOpen" title="Специальность" is-back @back="goBack">
 *     ...
 *   </CoreDrawer>
 */
import {
  toRefs, watch, useSlots, onBeforeUnmount,
} from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import scrollLock from 'src/utils/scrollLock'

const props = withDefaults(defineProps<{
  value: boolean
  title: string
  attach?: string | boolean
  width?: number
  titleTruncate?: boolean
  iconClose?: string
  isBack?: boolean
  dataQa?: string
}>(), {
  width: 400,
  attach: false,
  iconClose: 'ui-icon-close-not-a-circle',
  dataQa: 'core-drawer',
})

type Emits = {
  (_event: 'input', _value: boolean): void
  (_event: 'back'): void
}

const emit = defineEmits<Emits>()

const { value } = toRefs(props)
const slots = useSlots()
const isMobile = useIsMobile()

watch(value, val => {
  scrollLock.set(val)
})

onBeforeUnmount(() => {
  if (value.value) {
    scrollLock.set(false)
  }
})

function emitClose() {
  emit('input', false)
}

function emitBack() {
  emit('back')
}
</script>

<style lang="scss">
.core-drawer {
  max-height: 100% !important;
  border-radius: 0;
  margin: 0 0 0 auto;
}
</style>

<style lang="scss" scoped>
@import 'www/themes/doctors/common/variables';
@import 'www/themes/doctors/common/mixins';

.core-drawer {
  &__body {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100dvh;
    border-radius: 0;
    color: $ui-kit-text;
  }

  &__header {
    z-index: 1;
    padding: 8px;
    box-shadow: 0 1px 0 $ui-kit-bg-gray-60;
  }

  &__main {
    flex-grow: 1;
    cursor: unset;
    overflow-y: auto;
    margin: 4px 0;

    @include custom-scroll;
  }
}
</style>
